import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import {
  AttendanceStatsResponse,
  BatchWiseAttendanceResponse,
  CollegeBatchSummaryApiResponse,
  GetBatchesRequest,
  ParticipationTrendResponse,
} from "@src/pages/clients/client-dashboard/types";
import { GetBatchesResponse } from "@src/pages/batches/batch-base-component/data.type";

export const ClientDashboardApi = createApi({
  reducerPath: "clientDashboardApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ClientDashboard", "BatchDashboard"],

  endpoints: (builder) => ({
    getStudentAttendanceStats: builder.query<AttendanceStatsResponse, string>({
      query: (batchId) => ({
        url: `attendance/${batchId}/studentStats`,
        method: "GET",
      }),
      providesTags: ["ClientDashboard"],
    }),

    getStudentCollegeSummary: builder.query<
      any,
      { clientId: string; studentId: string }
    >({
      query: ({ clientId, studentId }) => ({
        url: `student/get-student-college-summary`,
        method: "GET",
        params: { clientId, studentId },
      }),
      providesTags: ["ClientDashboard"],
    }),

    getCollegeBatchSummary: builder.query<
      CollegeBatchSummaryApiResponse,
      { clientId: string }
    >({
      query: ({ clientId }) => ({
        url: `batch/get-college-batch-summary`,
        method: "GET",
        params: { clientId },
      }),
      providesTags: ["ClientDashboard"],
    }),

    getActiveBatches: builder.mutation<GetBatchesResponse, GetBatchesRequest[]>(
      {
        query: (body) => ({
          url: `branch/get-batches-multiple?status=Active`,
          method: "POST",
          body,
        }),
      },
    ),
    getBatchWiseAttendance: builder.query<
      BatchWiseAttendanceResponse,
      { clientId: string; batchIds?: string[] }
    >({
      query: ({ clientId, batchIds }) => ({
        url: `attendance/${clientId}/batch-wise-attendance`,
        method: "GET",
        params: batchIds && batchIds.length > 0 ? { batchIds } : undefined,
      }),
    }),
    getStudentSummary: builder.query<
      any,
      { clientId: string; studentId?: string }
    >({
      query: ({ clientId, studentId }) => ({
        url: `/student/get-student-college-summary?clientId=${clientId}&studentId=${studentId}`,
        method: "GET",
      }),
    }),
    getStudentAttendanceStatsForBatch: builder.query<
      AttendanceStatsResponse,
      string
    >({
      query: (batchId) => ({
        url: `attendance/${batchId}/studentStatsForBatch`,
        method: "GET",
      }),
      providesTags: ["BatchDashboard"],
    }),
    getStudentBatchSummary: builder.query<
      any,
      { batchId: string; studentId: string }
    >({
      query: ({ batchId, studentId }) => ({
        url: `student/get-student-batch-summary?batchId=${batchId}&studentId=${studentId}`,
        method: "GET",
      }),
    }),
    getAssessmentDashboard: builder.query<
     ParticipationTrendResponse,
      { batchId: string; fromDate: string; endDate: string }
    >({
      query: ({ batchId, fromDate, endDate }) => ({
        url: `batch/get-assessment-dashboard`,
        method: "GET",
        params: { batchId, fromDate, endDate },
      }),
      providesTags: ["BatchDashboard"],
    }),
    getAttendanceDashboard: builder.query<
     ParticipationTrendResponse,
      { batchId: string; fromDate: string; endDate: string }
    >({
      query: ({ batchId, fromDate, endDate }) => ({
        url: `batch/get-attendance-dashboard`,
        method: "GET",
        params: { batchId, fromDate, endDate },
      }),
      providesTags: ["BatchDashboard"],
    }),

    getAssessmentCompletionDashboard: builder.query<
      ParticipationTrendResponse,
      { batchId: string; fromDate: string; endDate: string }
    >({
      query: ({ batchId, fromDate, endDate }) => ({
        url: `batch/get-assessment-completion-dashboard`,
        method: "GET",
        params: { batchId, fromDate, endDate },
      }),
      providesTags: ["BatchDashboard"],
    }),
  }),
});

export const {
  useGetActiveBatchesMutation,
  useGetStudentAttendanceStatsQuery,
  useGetStudentCollegeSummaryQuery,
  useGetCollegeBatchSummaryQuery,
  useGetAssessmentDashboardQuery,
  useGetAssessmentCompletionDashboardQuery,
  useGetBatchWiseAttendanceQuery,
  useGetStudentSummaryQuery,
  useGetStudentAttendanceStatsForBatchQuery,
  useGetStudentBatchSummaryQuery,
  useGetAttendanceDashboardQuery,
} = ClientDashboardApi;
